import { useCallback, useEffect, useMemo, useState } from 'react';
import { Panel, TextInput, Button, Notice, ListCard, Dialog } from '@origo/origo-ui';
import type { Område, OmrådeParentType } from '@origo/seksjon';
import Eiendommer from '../../eiendommer/Eiendommer';
import type { EiendomsvelgerTarget } from '../../eiendommer/eiendomsvelgerTarget';
import { useOmråder } from '../../planer/hooks/useOmråder';
import { KonverterTilDelområdePanel } from './KonverterTilDelområdePanel';
import type { CopySource } from './KonverterTilDelområdePanel';
import { FlyttOmrådePanel, type MoveTarget } from './FlyttOmrådePanel';
export type { MoveTarget as MovedTarget };
import type { DelområdegruppeView } from './NavPanel';
import styles from './OmrådeEditorPanel.module.css';
import { FolderIcon, ListDashesIcon } from '@phosphor-icons/react';

interface OmrådeParentRef {
    parentType: OmrådeParentType;
    parentId: string;
}

interface OmrådeEditorPanelProps {
    planId: string;
    /** Settes når brukeren redigerer et eksisterende område. */
    initialOmråde?: Område;
    /**
     * Forelder for et nytt område. Settes når snarveien fra et delområde/
     * egendefinert område brukes, så det nye området nestes der. Hvis ikke
     * satt brukes Plan som forelder (toppnivå).
     */
    initialParent?: OmrådeParentRef;
    /** FKB delområde-tre — sendes videre til KonverterTilDelområdePanel. */
    delområder: Område[];
    /** Delområdegrupper — sendes videre til KonverterTilDelområdePanel. */
    delområdegrupper: readonly DelområdegruppeView[];
    /** ArcGIS layer-URL for eiendommer_v4 — sendes videre til Eiendommer-komponenten. */
    eiendommerUrl?: string;
    /** Kalles når brukeren lukker panelet. */
    onClose: () => void;
    /** Kalles etter en vellykket konvertering for å navigere brukeren til target delomr/gruppe. */
    onConverted?: (target: ConvertedTarget) => void;
    /** Kalles etter en vellykket flytt for å navigere brukeren til ny forelder. */
    onMoved?: (target: MoveTarget) => void;
    /** Kalles etter at brukeren har slettet området fra editoren. Lar AdminPage navigere til listevisning. */
    onDeleted?: () => void;
    /** Kalles etter enhver vellykket lagring (opprett/lagre navn/flytt). Lar AdminPage trigge publish. */
    onPublishAfterSave?: () => void;
}

export type ConvertedTarget =
    | { kind: 'delomrade'; id: string; navn: string; delomradetype: string; copiedFrom: CopySource }
    | { kind: 'gruppe'; gruppeId: string; navn: string; delomradetype: string; copiedFrom: CopySource }
    | { kind: 'delomradetype'; id: string; navn: string; copiedFrom: CopySource };

export function OmrådeEditorPanel({
    planId,
    initialOmråde,
    initialParent,
    delområder,
    delområdegrupper,
    eiendommerUrl,
    onClose,
    onConverted,
    onMoved,
    onDeleted,
    onPublishAfterSave,
}: Readonly<OmrådeEditorPanelProps>) {
    const { områder: egendefinerte, addOmråde, updateOmråde, deleteOmråde } = useOmråder(planId);

    /** Det "aktive" området — settes ved redigering, eller etter første lagring i opprettelse. */
    const [savedOmråde, setSavedOmråde] = useState<Område | null>(initialOmråde ?? null);
    const [navn, setNavn] = useState<string>(initialOmråde?.navn ?? '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [konverterOpen, setKonverterOpen] = useState(false);
    const [flyttOpen, setFlyttOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const isEdit = initialOmråde != null;

    const canSave = navn.trim().length > 0 && !saving;

    // Snarvei fra delområde/egendefinert område setter initialParent. Når den
    // ikke er satt (opprettelse fra "Egendefinerte områder"-toppnivået) blir
    // Plan forelder, og re-tilordning skjer kun via "Konverter til delområde".
    const parentRef = useMemo<OmrådeParentRef>(
        () => initialParent ?? { parentType: 'Plan', parentId: planId },
        [initialParent, planId]
    );

    // Konvertering kopierer kun seksjoner på selve området — barn-områder
    // håndteres ikke (skulle ellers blitt orphaned eller cascade-slettet
    // når bruker rydder kilden manuelt). Vi blokkerer konverteringen når
    // området har barn, og ber bruker flytte/slette dem først.
    const childOmråder = useMemo(
        () => savedOmråde
            ? egendefinerte.filter(
                (o) => o.parentType === 'Område' && o.parentId === savedOmråde.id
            )
            : [],
        [egendefinerte, savedOmråde]
    );
    const hasChildren = childOmråder.length > 0;

    const handleSave = useCallback(async () => {
        if (!canSave) return;
        setSaving(true);
        setError(null);
        try {
            if (savedOmråde && savedOmråde.objectId != null) {
                // Parent røres ikke her — flytt skjer eksplisitt via FlyttOmrådePanel.
                // Hadde vi sendt parentRef ville defaulten {Plan, planId} overskrevet
                // en parent som nettopp ble satt av en flytt.
                await updateOmråde(savedOmråde.objectId, navn.trim());
                setSavedOmråde({ ...savedOmråde, navn: navn.trim() });
            } else {
                await addOmråde(navn.trim(), parentRef);
                const newest = egendefinerte.find((o) => o.navn === navn.trim()) ?? null;
                setSavedOmråde(newest);
            }
            onPublishAfterSave?.();
        } catch (e) {
            setError((e as Error)?.message ?? 'Kunne ikke lagre område');
        } finally {
            setSaving(false);
        }
    }, [canSave, savedOmråde, navn, parentRef, addOmråde, updateOmråde, egendefinerte, onPublishAfterSave]);

    // Etter første lagring av et nytt område kommer det inn via useOmråder-listen.
    // Plukk opp det nyopprettede området slik at Eiendommer-seksjonen kan aktiveres.
    useEffect(() => {
        if (savedOmråde) return;
        if (isEdit) return;
        const candidate = egendefinerte.find((o) =>
            o.navn === navn.trim() &&
            o.parentType === parentRef.parentType &&
            o.parentId === parentRef.parentId
        );
        if (candidate) setSavedOmråde(candidate);
    }, [savedOmråde, isEdit, egendefinerte, navn, parentRef]);

    // Egendefinerte områder har ingen egen geometri — velgeren laster persisterte
    // add‑deltaer (geometry = null). Samme target‑modell og Eiendommer‑komponent
    // som de geografiske nivåene bruker, slik at UI‑et er identisk.
    const eiendommerTarget = useMemo<EiendomsvelgerTarget | null>(() => {
        if (!savedOmråde) return null;
        const numericId = Number(savedOmråde.id);
        if (!Number.isFinite(numericId)) return null;
        return {
            omradeId: String(numericId),
            omradetype: 'egendefinert',
            geometry: null,
        };
    }, [savedOmråde]);

    const handleConverted = useCallback((target: ConvertedTarget) => {
        setKonverterOpen(false);
        // Lukk editor og naviger brukeren til target delomr/gruppe.
        onConverted?.(target);
        onClose();
    }, [onConverted, onClose]);

    const handleMoved = useCallback((target: MoveTarget) => {
        setFlyttOpen(false);
        onMoved?.(target);
        onPublishAfterSave?.();
        onClose();
    }, [onMoved, onPublishAfterSave, onClose]);

    const handleDelete = useCallback(() => {
        setDeleteDialogOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!savedOmråde || savedOmråde.objectId == null) return;
        setSaving(true);
        setError(null);
        try {
            await deleteOmråde(savedOmråde.objectId, Number(savedOmråde.id));
            onDeleted?.();
            onClose();
        } catch (e) {
            setError((e as Error)?.message ?? 'Kunne ikke slette område');
            setSaving(false);
        } finally {
            setDeleteDialogOpen(false);
        }
    }, [savedOmråde, deleteOmråde, onDeleted, onClose]);

    if (savedOmråde && konverterOpen) {
        return (
            <KonverterTilDelområdePanel
                egendefinertOmråde={savedOmråde}
                planId={planId}
                delomradetypeAreas={delområder}
                delområdegrupper={delområdegrupper}
                onConvert={handleConverted}
                onClose={() => setKonverterOpen(false)}
            />
        );
    }

    if (savedOmråde && flyttOpen) {
        return (
            <FlyttOmrådePanel
                område={savedOmråde}
                planId={planId}
                delomradetypeAreas={delområder}
                onMoved={handleMoved}
                onCancel={() => setFlyttOpen(false)}
            />
        );
    }

    return (
        <>
            <Panel
                heading={isEdit ? 'Rediger egendefinert område' : 'Nytt egendefinert område'}
                closable
                onClose={onClose}
                footer={
                    <div className={styles.footerButtons}>
                        <Button variant="secondary" onClick={onClose} disabled={saving}>
                            {savedOmråde ? 'Ferdig' : 'Avbryt'}
                        </Button>
                        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
                            {saving ? 'Lagrer…' : savedOmråde ? 'Lagre endringer' : 'Opprett'}
                        </Button>
                    </div>
                }
            >
                <div className={styles.sections}>
                    <div className={styles.primarySections}>
                        <section className={styles.section}>
                            <h3 className={styles.sectionHeading}>Navn og eiendommer</h3>
                            <TextInput
                                label="Navn"
                                value={navn}
                                placeholder="Skriv inn navn på område"
                                onChange={(e) => setNavn(e.target.value)}
                            />
                            {error && <p className={styles.error}>{error}</p>}
                        </section>

                        <section className={styles.section}>
                            <h3 className={styles.sectionHeading}>Eiendommer</h3>
                            {eiendommerTarget ? (
                                <Eiendommer
                                    target={eiendommerTarget}
                                    eiendommerUrl={eiendommerUrl}
                                    flat
                                    showEmptyState
                                />
                            ) : (
                                <div className={styles.eiendommerPlaceholder}>
                                    Lagre området først for å velge eiendommer som definerer det.
                                </div>
                            )}
                        </section>
                    </div>

                    {isEdit && savedOmråde && (
                        <>
                            <section className={`${styles.section} ${styles.sectionBordered}`}>
                                <div><h3 className={styles.sectionHeading}>Flytt området eller veiledningspunkt</h3>
                                    <p className={styles.sectionHelp}>
                                        Legg dette området inn under et delområde i planen. Eiendommer og veiledninger blir med. Dersom veiledningspunktene egentlig hører til et delområde eller en gruppe i planen, kan du flytte bare de.
                                    </p></div>

                                {hasChildren && (
                                    <Notice variant="warning">
                                        Dette området har underliggende egendefinerte områder. Flytt eller slett dem først.
                                    </Notice>
                                )}
                                <div className={styles.listCardGroup}>
                                    <p className={styles.listCardLabel}>Velg hva du ønsker å flytte</p>
                                    <ListCard
                                        className={`${styles.listCard}${hasChildren ? ` ${styles.listCardDisabled}` : ''}`}
                                        prefixAlign="start"
                                        prefix={<FolderIcon weight="duotone" color='blue' />}
                                        title="Flytt hele området"
                                        description="Mappen legger seg under delområdet, med eiendommer og veiledningspunkter."
                                        onClick={hasChildren ? undefined : () => setFlyttOpen(true)}
                                    />
                                    <ListCard className={styles.listCard} prefixAlign="start" prefix={<ListDashesIcon weight="duotone" color='blue' />} title="Flytt bare veiledningene" description="Veiledningspunktene blir flyttet til delområdet." onClick={() => setKonverterOpen(true)} />
                                </div>
                                {/*
                                <Notice icon={<InfoIcon weight="duotone" />} variant="info" className={styles.noticeGap}>
                                    Dette kan du gjøre fra sidemenyen også, dra og slipp det egendefinerte området over området du ønsker å flytte område eller veiledningspunktene til.
                                </Notice>
                                */}
                            </section>

                            <section className={styles.section}>
                                <h3 className={styles.sectionHeading}>Slett område</h3>
                                {hasChildren ? (
                                    <Notice variant="warning">
                                        Dette området har {childOmråder.length} underliggende egendefinert
                                        {childOmråder.length === 1 ? 't' : 'e'} område
                                        {childOmråder.length === 1 ? '' : 'r'}. Slett eller flytt dem
                                        først for å unngå at de blir liggende uten forelder.
                                    </Notice>
                                ) : (
                                    <p className={styles.sectionHelp}>
                                        Slett området permanent. Veiledningspunktene som ligger direkte
                                        på området forsvinner.
                                    </p>
                                )}
                                <div>
                                    <Button
                                        variant="danger"
                                        onClick={handleDelete}
                                        disabled={hasChildren || saving}
                                    >
                                        Slett område
                                    </Button>
                                </div>
                            </section>
                        </>
                    )}
                </div>
            </Panel>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                title="Slett område"
                footer={
                    <div className={styles.dialogFooter}>
                        <Button variant="secondary" onClick={() => setDeleteDialogOpen(false)} disabled={saving}>
                            Avbryt
                        </Button>
                        <Button variant="danger" onClick={handleDeleteConfirm} disabled={saving}>
                            Slett område
                        </Button>
                    </div>
                }
            >
                <p>
                    Er du sikker på at du vil slette «{savedOmråde?.navn}»? Veiledningspunktene som
                    ligger direkte på området forsvinner.
                </p>
            </Dialog>
        </>
    );
}
